import React, { useCallback, useContext, useEffect, useState } from 'react';
import api from '../services/api';
import { AuthContext } from '../contexts/AuthContext';
import AlertModal from '../components/AlertModal';
import { AlertState, BandaCanal, BandaOrcamento } from '../types';
import './BandasOrcamento.css';

interface BandaFormState {
  valor: string; // em reais
  ativo: boolean;
}

const canalLabels: Record<BandaCanal, string> = {
  meta: 'Meta Ads',
  google: 'Google Ads',
  linkedin: 'LinkedIn Ads',
};

const canalOrder: BandaCanal[] = ['meta', 'google', 'linkedin'];

const centsToForm = (cents: number): string => (cents / 100).toFixed(2).replace('.', ',');

const formToCents = (valor: string): number => {
  const normalized = valor.replace(/\s|R\$/g, '').replace(/\./g, '').replace(',', '.');
  const parsed = Number(normalized);
  if (!normalized || Number.isNaN(parsed) || parsed < 0) return 0;
  return Math.round(parsed * 100);
};

const formatCents = (cents: number): string =>
  (cents / 100).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

const BandasOrcamento: React.FC = () => {
  const authContext = useContext(AuthContext);
  const { user } = authContext || {};
  const [bandas, setBandas] = useState<BandaOrcamento[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [forms, setForms] = useState<Record<BandaCanal, BandaFormState>>({
    meta: { valor: '0,00', ativo: false },
    google: { valor: '0,00', ativo: false },
    linkedin: { valor: '0,00', ativo: false },
  });
  const [savingCanal, setSavingCanal] = useState<BandaCanal | null>(null);
  const [alert, setAlert] = useState<AlertState>({ isOpen: false, message: '', type: 'error' });

  const canManage = user?.role === 'admin' || user?.role === 'manager';

  const loadBandas = useCallback(async (): Promise<void> => {
    try {
      const response = await api.get<BandaOrcamento[]>('/bandas-orcamento');
      setBandas(response.data);
      setForms((prev) => {
        const next = { ...prev };
        response.data.forEach((banda) => {
          next[banda.canal] = { valor: centsToForm(banda.maxDiarioCents), ativo: banda.ativo };
        });
        return next;
      });
    } catch (error) {
      console.error('Erro ao carregar bandas de orçamento:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadBandas();
  }, [loadBandas]);

  const handleValorChange = (canal: BandaCanal, valor: string): void => {
    setForms((prev) => ({ ...prev, [canal]: { ...prev[canal], valor } }));
  };

  const handleToggleAtivo = (canal: BandaCanal): void => {
    setForms((prev) => ({ ...prev, [canal]: { ...prev[canal], ativo: !prev[canal].ativo } }));
  };

  const handleSalvar = async (canal: BandaCanal): Promise<void> => {
    setSavingCanal(canal);
    try {
      const form = forms[canal];
      const response = await api.put<BandaOrcamento>(`/bandas-orcamento/${canal}`, {
        maxDiarioCents: formToCents(form.valor),
        ativo: form.ativo,
      });
      setBandas((prev) => prev.map((b) => (b.canal === canal ? response.data : b)));
      setAlert({
        isOpen: true,
        message: `Banda de ${canalLabels[canal]} atualizada${response.data.ativo ? '' : ' — canal continua bloqueado'}.`,
        type: 'success',
      });
    } catch (error: any) {
      setAlert({
        isOpen: true,
        message: error.response?.data?.message || 'Erro ao salvar a banda',
        type: 'error',
      });
    } finally {
      setSavingCanal(null);
    }
  };

  const bandasPorCanal = new Map(bandas.map((b) => [b.canal, b]));
  const totalAtivas = bandas.filter((b) => b.ativo).length;

  if (loading) {
    return (
      <div className="bandas-state">
        <div className="bandas-state-card">
          <span className="bandas-badge">Carregando</span>
          <h1>Preparando bandas de orçamento</h1>
          <p>Buscando os tetos diários configurados por canal.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bandas-page">
      <section className="bandas-hero">
        <div className="bandas-hero-copy">
          <span className="bandas-badge">Orçamento de mídia</span>
          <h1>Bandas de orçamento por canal</h1>
          <p>
            Defina o teto diário de gasto por canal e ative/desative cada um. Esta é a fonte
            única desses limites — nenhum outro sistema os define.
          </p>
        </div>
        <div className="bandas-hero-side">
          <div className="bandas-hero-card">
            <span className="bandas-hero-card-label">Status</span>
            <strong>{totalAtivas} de {canalOrder.length} canal(is) ativo(s)</strong>
            <p>Os demais estão bloqueados para os agentes de mídia.</p>
          </div>
        </div>
      </section>

      <div className="bandas-warning">
        <strong>Atenção</strong>
        <p>
          Estas bandas limitam o gasto dos agentes de mídia (acquisition-mcp). Banda inativa =
          canal bloqueado.
        </p>
      </div>

      <section className="bandas-grid">
        {canalOrder.map((canal) => {
          const banda = bandasPorCanal.get(canal);
          const form = forms[canal];
          const isSaving = savingCanal === canal;
          return (
            <article key={canal} className={`banda-card${form.ativo ? ' is-ativo' : ' is-inativo'}`}>
              <div className="banda-card-header">
                <div>
                  <span className={`banda-canal-badge is-${canal}`}>{canalLabels[canal]}</span>
                  <p className="banda-card-atual">
                    Atual: {banda ? formatCents(banda.maxDiarioCents) : formatCents(0)}/dia ·{' '}
                    {banda?.ativo ? 'ativo' : 'bloqueado'}
                  </p>
                </div>
                <button
                  type="button"
                  role="switch"
                  aria-checked={form.ativo}
                  aria-label={`${form.ativo ? 'Desativar' : 'Ativar'} canal ${canalLabels[canal]}`}
                  className={`banda-toggle${form.ativo ? ' is-on' : ''}`}
                  disabled={!canManage}
                  onClick={() => handleToggleAtivo(canal)}
                >
                  <span className="banda-toggle-thumb" />
                </button>
              </div>

              <div className="form-group">
                <label htmlFor={`banda-valor-${canal}`}>Teto diário (R$)</label>
                <input
                  id={`banda-valor-${canal}`}
                  className="premium-text-input"
                  type="text"
                  inputMode="decimal"
                  value={form.valor}
                  disabled={!canManage}
                  onChange={(e) => handleValorChange(canal, e.target.value)}
                  placeholder="Ex.: 300,00"
                />
              </div>

              <div className="banda-card-status">
                <span className={`banda-status-badge${form.ativo ? ' is-ativo' : ' is-inativo'}`}>
                  {form.ativo ? 'Canal liberado' : 'Canal bloqueado'}
                </span>
              </div>

              {canManage && (
                <button
                  type="button"
                  className="banda-save-button"
                  disabled={isSaving}
                  onClick={() => handleSalvar(canal)}
                >
                  {isSaving ? 'Salvando…' : 'Salvar banda'}
                </button>
              )}
            </article>
          );
        })}
      </section>

      <AlertModal
        isOpen={alert.isOpen}
        onClose={() => setAlert({ ...alert, isOpen: false })}
        message={alert.message}
        type={alert.type}
      />
    </div>
  );
};

export default BandasOrcamento;
